import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { ProductFilters, ActiveFilters } from '../components/ProductFilters';
import { SearchXIcon, AlertCircleIcon } from 'lucide-react';
import { useProducts } from '../hooks/useFirestore';
import { FirestoreProduct } from '../firebase/firestore';
import { ProductCard } from '../components/ProductCard';
// Map Firestore product to UI product
const mapFirestoreToProduct = (product: FirestoreProduct) => {
  return {
    id: product.id || '',
    title: product.title,
    description: product.description,
    price: product.price,
    category: product.category || 'Other',
    subcategory: product.subcategory,
    image: product.image || 'https://images.unsplash.com/photo-1561070791-2526d30994b5?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
    rating: product.rating || 4.5,
    seller: product.sellerName || 'Unknown Seller',
    platform: product.platform,
    licenseType: product.licenseType,
    useCase: product.useCase,
    isFree: product.price === 0 || product.isFree,
    format: product.format,
    skillLevel: product.skillLevel,
    industry: product.industry,
    creatorType: product.creatorType,
    isTrending: product.isTrending,
    isNewArrival: product.isNewArrival,
    isVideo: product.isVideo || product.category === 'Short-form Video',
    videoMetadata: product.videoMetadata
  };
};
// Skeleton loader for product cards
const ProductCardSkeleton = () => <div className="group relative bg-white dark:bg-[#162825] rounded-xl overflow-hidden shadow-sm" data-id="element-6274">
    <div className="relative aspect-square overflow-hidden bg-gray-200 dark:bg-[#1A3330]" data-id="element-6275"></div>
    <div className="p-5" data-id="element-6276">
      <div className="flex items-center justify-between mb-2" data-id="element-6277">
        <div className="flex items-center" data-id="element-6278">
          <div className="w-6 h-6 rounded-full bg-gray-200 dark:bg-[#1A3330] mr-2" data-id="element-6279"></div>
          <div className="h-3 w-16 bg-gray-200 dark:bg-[#1A3330] rounded" data-id="element-6280"></div>
        </div>
        <div className="flex items-center" data-id="element-6281">
          <div className="h-4 w-10 bg-gray-200 dark:bg-[#1A3330] rounded" data-id="element-6282"></div>
        </div>
      </div>
      <div className="h-5 w-3/4 bg-gray-200 dark:bg-[#1A3330] rounded mb-1" data-id="element-6283"></div>
      <div className="h-3 w-full bg-gray-200 dark:bg-[#1A3330] rounded mb-2" data-id="element-6284"></div>
      <div className="h-3 w-2/3 bg-gray-200 dark:bg-[#1A3330] rounded mb-3" data-id="element-6285"></div>
      <div className="flex items-center justify-between" data-id="element-6286">
        <div className="h-6 w-16 bg-gray-200 dark:bg-[#1A3330] rounded" data-id="element-6287"></div>
        <div className="h-8 w-28 bg-gray-200 dark:bg-[#1A3330] rounded-full" data-id="element-6288"></div>
      </div>
    </div>
  </div>;
// Empty state component for no matching products
const EmptyState = ({
  clearAllFilters
}: {
  clearAllFilters: () => void;
}) => <div className="bg-white dark:bg-[#162825] rounded-xl p-12 text-center" data-id="element-6289">
    <div className="w-16 h-16 bg-gray-100 dark:bg-[#1A3330] rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-6290">
      <SearchXIcon className="h-8 w-8 text-gray-400 dark:text-[#7A8F8A]" data-id="element-6291" />
    </div>
    <h3 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-6292">
      No matching products found
    </h3>
    <p className="text-gray-500 dark:text-[#7A8F8A] mb-6" data-id="element-6293">
      Try adjusting your filters or search term
    </p>
    <button onClick={clearAllFilters} className="inline-flex items-center px-4 py-2 bg-gray-100 dark:bg-[#1A3330] rounded-lg text-gray-700 dark:text-[#B0C1BD] hover:bg-gray-200 dark:hover:bg-[#0B2C2A] transition-colors" data-id="element-6294">
      Clear all filters
    </button>
  </div>;
// Error state component
const ErrorState = ({
  error,
  onRetry
}: {
  error: Error;
  onRetry: () => void;
}) => <div className="bg-white dark:bg-[#162825] rounded-xl p-12 text-center" data-id="element-6295">
    <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-6296">
      <AlertCircleIcon className="h-8 w-8 text-red-500 dark:text-red-400" data-id="element-6297" />
    </div>
    <h3 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-6298">
      Error loading products
    </h3>
    <p className="text-gray-500 dark:text-[#7A8F8A] mb-6" data-id="element-6299">
      {error.message || 'Something went wrong. Please try again.'}
    </p>
    <button onClick={onRetry} className="inline-flex items-center px-4 py-2 bg-[#2DB489] dark:bg-[#4ED1A0] rounded-lg text-white hover:bg-[#25A077] dark:hover:bg-[#3DC79D] transition-colors" data-id="element-6300">
      Try Again
    </button>
  </div>;
export const ProductCategoryPage = () => {
  const {
    category
  } = useParams<{
    category: string;
  }>();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [activeFilters, setActiveFilters] = useState<ActiveFilters>({
    categories: [],
    platforms: [],
    priceRanges: [],
    licenseTypes: [],
    useCases: [],
    formats: [],
    skillLevels: [],
    industries: [],
    creatorTypes: [],
    search: '',
    trending: false,
    newArrivals: false,
    subcategories: []
  });
  // Format the category name for display
  const formatCategoryName = (cat: string) => {
    return cat.replace(/-/g, ' ');
  };
  // Get the display category name
  const displayCategory = category ? formatCategoryName(category) : '';
  // Handle the case where the category in the URL is "short-form-videos" but the actual category is "Short-form Video"
  const actualCategory = displayCategory === 'short form videos' ? 'Short-form Video' : displayCategory;
  // Convert active filters to Firestore filters
  const convertToFirestoreFilters = useCallback(() => {
    const firestoreFilters: Record<string, any> = {};
    // Always filter by the current category
    if (actualCategory) {
      firestoreFilters.category = actualCategory;
    }
    return firestoreFilters;
  }, [actualCategory]);
  // Use the products hook with the category filter
  const {
    products: firestoreProducts,
    loading,
    initialLoading,
    error,
    hasMore,
    loadMore,
    updateFilters
  } = useProducts(12, convertToFirestoreFilters());
  // Convert Firestore products to UI products
  const products = firestoreProducts.map(mapFirestoreToProduct);
  // Handle scroll for navbar
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrolled]);
  // Update filters when category changes
  useEffect(() => {
    if (actualCategory) {
      updateFilters(convertToFirestoreFilters());
    }
  }, [actualCategory, convertToFirestoreFilters, updateFilters]);
  // Handle filter changes
  const handleFilterChange = (newFilters: ActiveFilters) => {
    setActiveFilters(newFilters);
  };
  // Clear all filters
  const clearAllFilters = useCallback(() => {
    setActiveFilters({
      categories: [],
      platforms: [],
      priceRanges: [],
      licenseTypes: [],
      useCases: [],
      formats: [],
      skillLevels: [],
      industries: [],
      creatorTypes: [],
      search: '',
      trending: false,
      newArrivals: false,
      subcategories: []
    });
  }, []);
  // Handle retry on error
  const handleRetry = () => {
    updateFilters(convertToFirestoreFilters());
  };
  // Render skeleton loaders during initial loading
  const renderSkeletons = () => {
    return Array(6).fill(0).map((_, index) => <ProductCardSkeleton key={`skeleton-${index}`} data-id="element-6301" />);
  };
  // Get category description
  const getCategoryDescription = () => {
    const descriptions: Record<string, string> = {
      'Short-form Video': 'Discover premium short-form videos perfect for TikTok, Instagram Reels, and YouTube Shorts. All videos are in vertical 1080×1920 format.',
      'Website Templates': 'Premium website templates for various platforms and industries.',
      'UI Kits': 'Professional UI kits for designers and developers.'
      // Add more category descriptions as needed
    };
    return descriptions[actualCategory] || `Browse our collection of ${actualCategory} digital products.`;
  };
  return <div className="w-full bg-gray-50 dark:bg-[#0A1C1A] min-h-screen" data-id="element-6302">
      <Navbar scrolled={scrolled} data-id="element-6303" />
      <main className="pt-24 pb-20 container mx-auto px-4 md:px-6 max-w-full overflow-x-hidden" data-id="element-6304">
        <div className="text-center mb-12" data-id="element-6305">
          <h1 className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] text-transparent bg-clip-text" data-id="element-6306">
            {actualCategory}
          </h1>
          <p className="text-xl text-gray-600 dark:text-[#B0C1BD] max-w-3xl mx-auto" data-id="element-6307">
            {getCategoryDescription()}
          </p>
        </div>

        {/* Product grid with fixed height to prevent layout shifts */}
        <div className="w-full product-grid-container" style={{
        minHeight: '50vh'
      }} data-id="element-6308">
          {initialLoading ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-id="element-6309">
              {renderSkeletons()}
            </div> : error ? <ErrorState error={error} onRetry={handleRetry} data-id="element-6310" /> : products.length > 0 ? <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-id="element-6311">
                {products.map(product => <ProductCard key={product.id} product={product} data-id="element-6312" />)}
              </div>
              {/* Load more button */}
              {hasMore && <div className="text-center mt-12" data-id="element-6313">
                  <button className="bg-white dark:bg-[#162825] border border-gray-200 dark:border-[#1A3330] text-gray-800 dark:text-[#F8FAF9] px-8 py-3 rounded-full text-lg font-medium hover:border-[#2DB489] hover:text-[#2DB489] dark:hover:border-[#4ED1A0] dark:hover:text-[#4ED1A0] disabled:opacity-50 disabled:cursor-not-allowed" onClick={loadMore} disabled={loading} data-id="element-6314">
                    {loading ? <span className="flex items-center justify-center" data-id="element-6315">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-[#2DB489] dark:text-[#4ED1A0]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-6316">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-6317"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-6318"></path>
                        </svg>
                        Loading...
                      </span> : 'Load More Products'}
                  </button>
                </div>}
            </> : <EmptyState clearAllFilters={clearAllFilters} data-id="element-6319" />}
        </div>
      </main>
      <Footer data-id="element-6320" />
    </div>;
};